import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { PACKAGE_IMAGES } from "@/lib/api";

export default function Hero() {
    return (
        <section
            className="relative md:min-h-screen md:flex md:items-end overflow-hidden"
            data-testid="hero-section"
        >
            {/* MOBILE LAYOUT: full image visible on top, text below */}
            <div className="md:hidden">
                <div className="relative pt-20 bg-black">
                    <img
                        src={PACKAGE_IMAGES.studio}
                        alt="Studio Mon Séraphin"
                        className="w-full h-auto object-contain"
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black to-transparent" />
                </div>

                <div className="relative px-6 pt-8 pb-20 bg-black">
                    <div className="pointer-events-none absolute -left-24 top-10 w-[400px] h-[400px] rounded-full bg-[radial-gradient(circle_at_center,rgba(245,183,84,0.18),transparent_70%)] blur-2xl" />
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="relative font-display text-3xl font-light leading-[1.1] tracking-tight text-white"
                    >
                        Votre voix,
                        <br />
                        <span className="text-amber-300/95">au niveau studio.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.25 }}
                        className="relative mt-4 font-body text-sm text-zinc-400 leading-relaxed"
                    >
                        Studio podcast audio & vidéo clé-en-main pour entrepreneurs,
                        créateurs et marques.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="relative mt-6 flex flex-col gap-3"
                    >
                        <Link
                            to="/reserver"
                            className="group inline-flex items-center justify-center gap-2 rounded-md bg-amber-300 px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-amber-200 transition-colors"
                            data-testid="hero-reserver-btn-mobile"
                        >
                            Réserver une session
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <a
                            href="#formules"
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/15 px-6 py-3 text-sm text-white hover:border-white/40 hover:bg-white/5 transition-colors"
                        >
                            Voir les formules
                        </a>
                    </motion.div>
                    <div className="relative mt-8 flex items-center gap-2 text-xs text-zinc-400 font-body">
                        <MapPin className="w-3.5 h-3.5 text-amber-400" />
                        <span>Noisy-le-Grand — RER A Mont d'Est</span>
                    </div>
                    <div className="relative mt-6 flex items-center gap-8 pt-6 border-t border-white/5">
                        <div>
                            <p className="font-display text-lg text-white font-light leading-tight">4k+</p>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Épisodes enregistrés</p>
                        </div>
                        <div>
                            <p className="font-display text-lg text-white font-light leading-tight">24h</p>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Livraison express</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* DESKTOP LAYOUT: full-screen immersive */}
            <div className="hidden md:block absolute inset-0">
                <img
                    src={PACKAGE_IMAGES.studio}
                    alt="Studio Mon Séraphin"
                    className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/30" />
            </div>

            <div className="hidden md:block pointer-events-none absolute -left-40 top-1/3 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle_at_center,rgba(245,183,84,0.25),transparent_70%)] blur-2xl" />

            <div className="hidden md:block relative z-10 max-w-7xl mx-auto px-12 pt-28 pb-32 w-full">
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.15 }}
                    className="font-display text-4xl lg:text-6xl font-light leading-[1.05] tracking-tight text-white max-w-3xl"
                >
                    Votre voix,
                    <br />
                    <span className="text-amber-300/95">au niveau studio.</span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.35 }}
                    className="mt-4 max-w-md font-body text-base text-zinc-300/90 leading-relaxed"
                >
                    Studio podcast audio & vidéo clé-en-main pour entrepreneurs,
                    créateurs et marques.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="mt-6 flex flex-row gap-3"
                >
                    <Link
                        to="/reserver"
                        className="group inline-flex items-center justify-center gap-2 rounded-md bg-amber-300 px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-amber-200 transition-colors duration-200"
                        data-testid="hero-reserver-btn"
                    >
                        Réserver une session
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                    <a
                        href="#formules"
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-white/15 px-6 py-3 text-sm text-white hover:border-white/40 hover:bg-white/5 transition-colors"
                        data-testid="hero-voir-formules-btn"
                    >
                        Voir les formules
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.2, delay: 0.8 }}
                    className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-zinc-400 font-body"
                >
                    <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-amber-400" />
                        <span>Villiers-sur-Marne · à 13 min du RER A Noisy-le-Grand</span>
                    </div>
                    <div className="h-3 w-px bg-white/20" />
                    <div className="flex items-center gap-5">
                        <div>
                            <p className="font-display text-lg text-white font-light leading-tight">4k+</p>
                            <p className="text-xs uppercase tracking-widest text-zinc-500">Épisodes enregistrés</p>
                        </div>
                        <div>
                            <p className="font-display text-lg text-white font-light leading-tight">24h</p>
                            <p className="text-xs uppercase tracking-widest text-zinc-500">Livraison express</p>
                        </div>
                    </div>
                </motion.div>
            </div>

            <div className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 text-zinc-500 text-xs font-body tracking-[0.3em] uppercase">
                <div className="flex flex-col items-center gap-3">
                    <span>Scroll</span>
                    <div className="w-px h-10 bg-gradient-to-b from-amber-400/60 to-transparent" />
                </div>
            </div>
        </section>
    );
}
